import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import { useOutletContext, useSearchParams } from "react-router-dom";
import CommonTable from "../common/commonTable";
import "../../styles/institution/institutionTeacher.scss";
import GlobalLoader from "../common/GlobalLoader";
import Pagination from "../common/Pagination";
import {
  createInstitutionTeacher,
  createTeacherInstitution,
  deleteTeacherInstitution,
  getInstitutionTeacher,
  updateTeacherInstitution,
  resetTeacherInstitutionPassword,
} from "../../helper/apicalls/institution/institutionTeacher";
import {
  getAllBatchInstitution,
  getAllBatchTeacherInstitution,
  getNotAssignBatchTeacherInstitution,
  updateAssignBatchTeacherInstitution,
} from "../../helper/apicalls/institution/institutionBatch";
import TeacherForm from "../admin/TeacherForm";
import TeacherAssignForm from "../admin/TeacherAssignForm";
import SuccessPopup from "../common/SuccessPopup";
import TeacherSelectForm from "../admin/TeacherSelectForm";
import ResetPasswordPopup from "../common/ResetPasswordPopup";
import EditTeacherSubjectsModal from "../admin/EditTeacherSubjectsModal";
import EditIcon from "@mui/icons-material/Edit";
import {
  getAllInstitutionCourse,
  getAllInstitutionCourseBatch,
  assignTeacherSubjects,
  getOneBatch,
} from "../../helper/apicalls/institution";
import { getSubjectsByCourseKey } from "../../helper/apicalls/course";

const BatchCell = ({ batch_count, batches, role }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!batch_count || batch_count === 0 || !batches || batches.length === 0) {
    return (
      <span
        className={`institution-teacher-assign-status-badge ${
          role === "admin" ? "" : "institution-teacher-assign-status-inactive"
        }`}
      >
        {role === "admin" ? "-" : "Not assigned"}
      </span>
    );
  }

  const firstBatch = batches[0];
  const remainingBatches = batches.slice(1);

  return (
    <div className="batch-cell-wrapper" ref={containerRef}>
      <span className="batch-first-name" title={firstBatch.name}>
        {firstBatch.name}
      </span>
      {batch_count > 1 && (
        <span
          className="batch-count-tag"
          onClick={(e) => {
            e.stopPropagation();
            setShowTooltip(!showTooltip);
          }}
        >
          +{batch_count - 1}
        </span>
      )}
      {showTooltip && (
        <div className="batch-floating-tooltip">
          {remainingBatches.map((b, idx) => (
            <div key={b.id || idx} className="batch-tooltip-item">
              {b.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InstitutionTeacher = ({ batch, batchId }) => {
  const authToken = Cookies.get("institution_token");
  const admin_authToken = Cookies.get("admin_token");
  const [searchParams] = useSearchParams();
  const permissions = Cookies.get("permissions");
  let permissionsData = [];
  try {
    const parsed = permissions ? JSON.parse(permissions) : [];
    permissionsData = Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Error parsing permissions:", e);
  }
  const hasBatchPermission = permissionsData?.find(
    (permission) => permission?.permission_type === "batches"
  );
  const hasTeacherPermission = permissionsData?.find(
    (permission) => permission?.permission_type === "teacher"
  );

  const [loader, setLoader] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const { setHeaderName, setIsShowSearch } = useOutletContext();
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: "", role: "" });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentTeacher, setCurrentTeacher] = useState(null);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [isTeacherPickerOpen, setIsTeacherPickerOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("Teacher created successfully");
  const [institutionCourseData, setInstitutionCourseData] = useState([]);
  const [batchesList, setBatchesList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [isEditSubjectsOpen, setIsEditSubjectsOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [resetPopupOpen, setResetPopupOpen] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState(null);

  const handleResetPasswordClick = (row) => {
    setResetTargetUser(row);
    setResetPopupOpen(true);
  };

  const handleResetPasswordSubmit = async (newPassword) => {
    if (!resetTargetUser) return;
    setLoader(true);
    try {
      const res = await resetTeacherInstitutionPassword(authToken, {
        password: newPassword,
      }, resetTargetUser.id);
      setSuccessMsg(res?.message || "Password reset successfully");
      setSuccessOpen(true);
    } catch (err) {
      console.error("Failed to reset password:", err);
      setSuccessMsg(err?.message || "Failed to reset password");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
      setResetPopupOpen(false);
      setResetTargetUser(null);
    }
  };

  useEffect(() => {
    if (batch && batchId) {
      getOneBatch(authToken, batchId)
        .then((batchData) => {
          const key = batchData?.course?.key;
          if (key) {
            fetchCourseSubjects(key);
          }
        })
        .catch((err) => console.error("Error loading batch course for subjects:", err));
    }
  }, [batch, batchId]);

  const searchDebounceRef = useRef(null);
  const handleSearchChange = (val) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(val);
    }, 300);
  };

  const columns = useMemo(() => {
    const columsData = [
      { field: "name", label: "Name" },
      { field: "email", label: "Email" },
    ];
    if (!batch) {
      columsData.push(
        {
          field: "batch_count",
          label: "Batch",
          align: "center",
          render: (batch_count, row) => (
            <span
              className={`institution-teacher-assign-status-badge ${
                batch_count > 0
                  ? "institution-teacher-assign-status-active"
                  : "institution-teacher-assign-status-inactive"
              }`}
            >
              {batch_count > 0
                ? "Assigned"
                : row.role === "admin"
                ? "-"
                : "Not assigned"}
            </span>
          ),
        },
        {
          field: "batch_count",
          label: "Batch Count",
          align: "center",
          render: (batch_count, row) => (
            <span className="batch-count-display">
              {row.role === "admin" && (!batch_count || batch_count === 0)
                ? "-"
                : batch_count || 0}
            </span>
          ),
        },
        {
          field: "batches",
          label: "Batch Names",
          render: (batches, row) => (
            <BatchCell
              batch_count={row.batch_count}
              batches={batches}
              role={row.role}
            />
          ),
        }
      );
    }
    columsData.push({
      field: "role",
      label: "Role",
      render: (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : "-",
    });
    columsData.push({
      field: "disabled",
      label: "Status",
      align: "center",
      render: (isDisabled) => (
        <span
          className={`status-badge ${
            isDisabled ? "status-inactive" : "status-active"
          }`}
        >
          {isDisabled ? "Disabled" : "Active"}
        </span>
      ),
    });
    if (batch) {
      columsData.push({
        field: "subjects",
        label: "Subjects",
        render: (subjects) => {
          if (!subjects || subjects.length === 0) return "-";
          return subjects.map((s) => s.name).join(", ");
        },
      });
    }

    if (hasTeacherPermission?.write) {
      columsData.push({
        field: "reset_password",
        label: "Reset Password",
        align: "center",
        render: (_, row) => (
          <button
            className="reset-password-btn-table"
            onClick={(e) => {
              e.stopPropagation();
              handleResetPasswordClick(row);
            }}

          >
            Reset
          </button>
        ),
      });
    }

    return columsData;
  }, [batch, hasTeacherPermission]);

  const fetchInstitutionTeacherList = async () => {
    setLoader(true);
    try {
      const s = (filters?.status || "").trim();
      const params = { page, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (filters?.role) params.role = filters.role;
      if (s === "active") params.disabled = false;
      else if (s === "disabled") params.disabled = true;

      const res = await getInstitutionTeacher(authToken, params);
      setTeachers(Array.isArray(res?.data) ? res.data : []);
      const tp = Math.ceil(Number(res.total) / limit) || 1;
      setTotalPages(tp);
    } catch (err) {
      console.error("Failed to fetch teachers:", err);
    } finally {
      setLoader(false);
    }
  };

  const fetchBatchTeachersList = async () => {
    setLoader(true);
    try {
      const s = (filters?.status || "").trim();
      const params = { page, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (filters?.role) params.role = filters.role;
      if (batchId) params.batchId = batchId;
      if (s === "active") params.disabled = false;
      else if (s === "disabled") params.disabled = true;

      const res = await getAllBatchTeacherInstitution(authToken, params);
      setTeachers(Array.isArray(res?.data) ? res.data : []);
      const tp = Math.ceil(Number(res.total) / limit) || 1;
      setTotalPages(tp);
    } catch (err) {
      console.error("Failed to fetch teachers:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleEdit = (id, row) => {
    setCurrentTeacher(row);
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    setLoader(true);
    try {
      if (!batch) {
        await deleteTeacherInstitution(authToken, id);
        await fetchInstitutionTeacherList();
      } else {
        const res = await updateAssignBatchTeacherInstitution(
          authToken,
          { remove_teachers: [id] },
          batchId
        );
        setSuccessMsg(res?.message || "Teachers remove successfully");
        setSuccessOpen(true);
        await fetchBatchTeachersList();
      }
    } catch (err) {
      console.error("Failed to delete teacher:", err);
    } finally {
      setLoader(false);
    }
  };
  const handleEditSubjectsSubmit = async (subject_ids) => {
    if (!editingTeacher || !batchId) return;
    setLoader(true);
    try {
      await assignTeacherSubjects(authToken, {
        batch_id: batchId,
        teacher_id: editingTeacher.id,
        subject_ids,
      });
      setSuccessMsg("Teacher subjects updated successfully");
      setSuccessOpen(true);
      setIsEditSubjectsOpen(false);
      setEditingTeacher(null);
      await fetchBatchTeachersList();
    } catch (err) {
      console.error("Failed to update teacher subjects:", err);
      setSuccessMsg(err?.message || "Failed to update teacher subjects");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
    }
  };

  const handleCreateClick = () => {
    setCurrentTeacher(null);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setCurrentTeacher(null);
  };

  const handleFormSubmit = async (data) => {
    setLoader(true);
    if (!currentTeacher) {
      try {
        const payload = {
          email: data.email,
          name: data.name,
          password: data.password,
        };

        const res = await createTeacherInstitution(authToken, payload);
        console.log(res, "created");
        handleFormClose();
        setSuccessOpen(true);
        setSuccessMsg(res?.message || "Teacher created successfully");
        await fetchInstitutionTeacherList();
      } catch (err) {
        console.error("Failed to create teacher:", err);
      } finally {
        setLoader(false);
      }
    } else {
      try {
        const payload = {
          name: data.name,
        };
        const res = await updateTeacherInstitution(
          authToken,
          payload,
          currentTeacher?.id
        );
        console.log(res, "update");
        handleFormClose();
        setSuccessOpen(true);
        setSuccessMsg(res?.message || "Teacher created successfully");
        await fetchInstitutionTeacherList();
      } catch (err) {
        console.error("Failed to create teacher:", err);
      } finally {
        setLoader(false);
      }
    }
  };

  const handleOpenAssign = () => {
    if (!selectedTeacherIds?.length) return;
    setIsAssignOpen(true);
  };

  const handleAssignClose = () => setIsAssignOpen(false);

  const handleAssignSubmit = async ({ teacherIds, courseId, batchId, subject_ids }) => {
    try {
      const res = await updateAssignBatchTeacherInstitution(
        authToken,
        { teachers: teacherIds },
        batchId
      );
      // Assign subjects per teacher if any were selected
      if (subject_ids && subject_ids.length > 0 && batchId) {
        await Promise.all(
          teacherIds.map((teacherId) =>
            assignTeacherSubjects(authToken, {
              batch_id: batchId,
              teacher_id: teacherId,
              subject_ids,
            })
          )
        );
      }
      setSuccessMsg(res?.message || "Teachers assigned successfully");
      setSuccessOpen(true);
      setIsAssignOpen(false);
      await fetchInstitutionTeacherList();
      setSelectedTeacherIds([]);
    } catch (err) {
      console.error("Failed to update batch teachers:", err);
    }
  };

  const goToPage = (p) => {
    const next = Math.max(1, p);
    setLoader(true);
    setPage(next);
  };

  const changePerPage = (pp) => {
    setLoader(true);
    setLimit(pp);
    setPage(1);
  };

  const handleAddTeacherClick = async () => {
    if (!batch || !batchId) return;
    setIsTeacherPickerOpen(true);
    setLoader(true);
    try {
      const [res, batchData] = await Promise.all([
        getNotAssignBatchTeacherInstitution(authToken, batchId),
        getOneBatch(authToken, batchId),
      ]);
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setAvailableTeachers(list);
      const courseKey = batchData?.course?.key;
      if (courseKey) {
        fetchCourseSubjects(courseKey);
      } else {
        setSubjectsList([]);
      }
    } catch (err) {
      console.error("Failed to load teachers for selection:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleAddTeacherClose = () => {
    setIsTeacherPickerOpen(false);
    setSubjectsList([]);
  };

  const handleAddTeacherSubmit = async ({ teacherIds, subject_ids }) => {
    if (!Array.isArray(teacherIds) || teacherIds.length === 0) return;
    setLoader(true);
    try {
      const res = await updateAssignBatchTeacherInstitution(
        authToken,
        { teachers: teacherIds },
        batchId
      );
      // Assign subjects per teacher if any were selected
      if (subject_ids && subject_ids.length > 0 && batchId) {
        await Promise.all(
          teacherIds.map((teacherId) =>
            assignTeacherSubjects(authToken, {
              batch_id: batchId,
              teacher_id: teacherId,
              subject_ids,
            })
          )
        );
      }
      setSuccessMsg(res?.message || "Teachers assigned successfully");
      setSuccessOpen(true);
      setIsTeacherPickerOpen(false);
      await fetchBatchTeachersList();
    } catch (err) {
      console.error("Failed to assign teachers:", err);
      setSuccessMsg(err?.message || "Failed to assign teachers");
      setSuccessOpen(true);
    } finally {
      setLoader(false);
      setIsTeacherPickerOpen(false);
    }
  };

  const fetchInstitutionCourse = async () => {
    try {
      const res = await getAllInstitutionCourse(authToken, undefined, {});
      setInstitutionCourseData(res[0]?.course || []);
    } catch (err) {
      console.error("Failed to fetch teachers:", err);
    } finally {
    }
  };
  const fetchCourseBatch = async (key) => {
    setLoader(true);
    try {
      const res = await getAllBatchInstitution(authToken, key, {});
      setBatchesList(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch batches:", err);
    } finally {
      setLoader(false);
    }
  };

  const fetchCourseSubjects = async (courseKey) => {
    try {
      const res = await getSubjectsByCourseKey(courseKey);
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      setSubjectsList(list);
    } catch (err) {
      console.error("Failed to fetch subjects:", err);
      setSubjectsList([]);
    }
  };

  useEffect(() => {
    if (!batch) {
      setHeaderName("Teachers");
      fetchInstitutionCourse();
    }

    setIsShowSearch && setIsShowSearch(false);
  }, []);

  useEffect(() => {
    if (batch && batchId) {
      fetchBatchTeachersList();
      return;
    } else {
      fetchInstitutionTeacherList();
    }
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [page, limit, searchTerm, filters]);

  return (
    <div className="institution-teacher-tab-container">
      {loader && <GlobalLoader visible={loader} text="Loading teachers..." />}
      <div className="institution-teacher-tab">
        <CommonTable
          data={teachers}
          columns={columns}
          getRowId={(r) => r.id}
          isRowSelectable={(row) => row.role !== "admin"}
          showToolbar
          title={"Teachers List"}
          onSearchChange={handleSearchChange}
          showFilter
          filterSections={[
            {
              key: "status",
              title: "Status",
              multiple: false,
              options: [
                { value: "", label: "All" },
                { value: "active", label: "Active" },
                { value: "disabled", label: "Disabled" },
              ],
            },
          ]}
          toolbarRight={
            <>
              {!batch && hasTeacherPermission?.write && (
                <>
                  <button
                    className="institution-teacher-tab-header-btn btn-interactive"
                    onClick={handleCreateClick}
                  >
                    Create
                  </button>

                  <button
                    className={`institution-teacher-tab-header-btn teacher-btn-interactive btn-interactive`}
                    onClick={handleOpenAssign}
                    disabled={
                      !(selectedTeacherIds && selectedTeacherIds.length > 0)
                    }
                  >
                    Assign
                  </button>
                </>
              )}
              {batch && hasBatchPermission?.write && (
                <button
                  className={`institution-teacher-tab-header-btn teacher-btn-interactive btn-interactive`}
                  onClick={handleAddTeacherClick}
                >
                  Add Teachers
                </button>
              )}
            </>
          }
          onEdit={handleEdit}
          onDelete={handleDelete}
          filterValues={filters}
          onFilterChange={setFilters}
          extraActions={(row, rowKey) => {
            const items = [];
            if (batch) {
              items.push({
                key: "edit-subjects",
                label: "Edit Subjects",
                icon: <EditIcon fontSize="small" />,
                onClick: () => {
                  setEditingTeacher(row);
                  setIsEditSubjectsOpen(true);
                },
              });
            }
            return items;
          }}
          multiSelect={
            batch
              ? false
              : !batch && !hasTeacherPermission?.write
              ? false
              : true
          }
          selectedRowIds={selectedTeacherIds}
          onSelectionChange={setSelectedTeacherIds}
          showActions={
            batch && !hasBatchPermission?.write
              ? false
              : !batch && !hasTeacherPermission?.write
              ? false
              : true
          }
          maxBodyHeight={
            batch ? `calc(-300px + 100vh)` : `calc(-230px + 100vh)`
          }
          fixedBodyRows={batch ? 5 : 8}
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          actionLabelDelete={batch ? "Remove" : "Delete"}
          deleteMessage={
            batch
              ? "Are you sure you want to remove from this batch? This action cannot be undone."
              : "Are you sure you want to delete this item? This action cannot be undone."
          }
          removeActionLabel={batch ? "Remove from Batch" : "Delete"}
          showEditAction={batch ? false : true}
        />
      </div>
      <div className="institution-teacher-pagination">
        <Pagination
          page={page}
          pageCount={totalPages}
          onChange={goToPage}
          perPage={limit}
          perPageOptions={[5, 10, 25, 100]}
          onPerPageChange={changePerPage}
        />
      </div>
      {isFormOpen && (
        <TeacherForm
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          initialData={currentTeacher}
          isInstitution={true}
        />
      )}
      {isTeacherPickerOpen && (
        <TeacherSelectForm
          isOpen={isTeacherPickerOpen}
          onClose={handleAddTeacherClose}
          onSubmit={handleAddTeacherSubmit}
          teachers={availableTeachers}
          subjects={subjectsList}
          title="Add Teachers"
        />
      )}
      {isAssignOpen && (
        <TeacherAssignForm
          isOpen={isAssignOpen}
          onClose={handleAssignClose}
          onSubmit={handleAssignSubmit}
          selectedTeacherIds={selectedTeacherIds}
          courses={institutionCourseData}
          batches={batchesList}
          subjects={subjectsList}
          getBatched={fetchCourseBatch}
          getSubjects={fetchCourseSubjects}
        />
      )}
      {successOpen && (
        <SuccessPopup
          open={successOpen}
          onClose={() => setSuccessOpen(false)}
          message={successMsg}
        />
      )}
      {isEditSubjectsOpen && editingTeacher && (
        <EditTeacherSubjectsModal
          isOpen={isEditSubjectsOpen}
          onClose={() => {
            setIsEditSubjectsOpen(false);
            setEditingTeacher(null);
          }}
          onSubmit={handleEditSubjectsSubmit}
          teacherName={editingTeacher.name}
          subjects={subjectsList}
          initialSelectedSubjects={editingTeacher.subjects || []}
        />
      )}
      {resetPopupOpen && resetTargetUser && (
        <ResetPasswordPopup
          isOpen={resetPopupOpen}
          onClose={() => {
            setResetPopupOpen(false);
            setResetTargetUser(null);
          }}
          userName={resetTargetUser.name}
          onResetPassword={handleResetPasswordSubmit}
        />
      )}
    </div>
  );
};

export default InstitutionTeacher;
